import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useErrorMessages, useUserSession } from '../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../models/Logging';
import { getMetaAuthFailureReason } from '../../../../models/utils/metaAuthFailure';
import {
	useConfigureMetaLeads,
	useGetMetaAdAccounts,
	useGetMetaAdsProvider,
	useGetMetaPages,
} from '../../../../queries/Ads';
import { Button } from '../../../components-v2/Button';
import { Checkbox } from '../../../components-v2/Checkbox';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { MetaAuthFailureNotice } from '../../../components/MetaAuthFailureNotice';
import { IModalProps, Modal } from '../../../components/Modal';
import { RadioButton } from '../../../components/RadioButton';
import { styleSheet } from './styles';

interface IFacebookAdsModalProps {
	modalProps: IModalProps;
	onSaveSuccess?: () => void;
	onReconnect?: () => void;
}

type Step = 'adAccount' | 'pages';

export const FacebookAdsModal = ({ modalProps, onSaveSuccess, onReconnect }: IFacebookAdsModalProps) => {
	const userSession = useUserSession();
	const errorMessages = useErrorMessages();
	const { logApiError } = useEventLogging('FacebookAdsModal');

	const [step, setStep] = useState<Step>('adAccount');
	const [selectedAdAccountId, setSelectedAdAccountId] = useState<string | null>(null);
	const [selectedPageIds, setSelectedPageIds] = useState<Set<string>>(new Set());

	const { data: metaAdsProvider, isPending } = useGetMetaAdsProvider({
		impersonationContext: userSession.impersonationContext,
	});

	const isConnected = metaAdsProvider?.enabled ?? false;

	const {
		data: adAccounts,
		isLoading: isLoadingAccounts,
		error: accountsError,
	} = useGetMetaAdAccounts({
		impersonationContext: userSession.impersonationContext,
		enabled: isConnected && modalProps.isOpen,
	});

	const {
		data: pages,
		isLoading: isLoadingPages,
		error: pagesError,
	} = useGetMetaPages({
		impersonationContext: userSession.impersonationContext,
		enabled: isConnected && modalProps.isOpen,
	});
	const accountsAuthFailureReason = getMetaAuthFailureReason(accountsError);
	const pagesAuthFailureReason = getMetaAuthFailureReason(pagesError);

	const onError = (operationName: string, error: Api.IOperationResultNoValue) => {
		errorMessages.push({
			messages: [`Failed to ${operationName}`],
		});
		logApiError(operationName, error);
	};

	const { mutate: configureMetaLeads, isPending: isSaving } = useConfigureMetaLeads({
		onError: error => onError('Configure', error),
		onSuccess: () => {
			toast.success('Meta Ads configuration saved successfully!');
			onSaveSuccess?.();
			modalProps.onRequestClose(null, false);
		},
	});

	useEffect(() => {
		if (modalProps.isOpen && metaAdsProvider) {
			if (metaAdsProvider.adAccountIds && metaAdsProvider.adAccountIds.length > 0) {
				setSelectedAdAccountId(metaAdsProvider.adAccountIds[0]);
			} else {
				setSelectedAdAccountId(null);
			}
			if (metaAdsProvider.pageIds && metaAdsProvider.pageIds.length > 0) {
				setSelectedPageIds(new Set(metaAdsProvider.pageIds));
				setStep('pages');
			} else {
				setSelectedPageIds(new Set());
				setStep('adAccount');
			}
		} else if (!modalProps.isOpen) {
			setSelectedAdAccountId(null);
			setSelectedPageIds(new Set());
			setStep('adAccount');
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [modalProps.isOpen]);

	const handlePageToggle = (pageId: string) => {
		setSelectedPageIds(prev => {
			const next = new Set(prev);
			if (next.has(pageId)) {
				next.delete(pageId);
			} else {
				next.add(pageId);
			}
			return next;
		});
	};

	const handleNext = () => {
		if (!selectedAdAccountId) {
			errorMessages.push({ messages: ['Please select an ad account'] });
			return;
		}
		setStep('pages');
	};

	const handleBack = () => {
		setStep('adAccount');
	};

	const handleSave = () => {
		if (!selectedAdAccountId) {
			errorMessages.push({ messages: ['Please select an ad account'] });
			return;
		}

		if (selectedPageIds.size === 0) {
			errorMessages.push({ messages: ['Please select at least one page'] });
			return;
		}

		configureMetaLeads({
			adAccountIds: [selectedAdAccountId],
			pageIds: [...selectedPageIds],
		});
	};

	const handleClose = () => {
		modalProps.onRequestClose(null, true);
	};

	if (isPending) {
		return (
			<Modal {...modalProps} useDefaultHeader={true}>
				<div className={css(styleSheet.loadingContainer)}>
					<LoadingSpinner type='large' />
				</div>
			</Modal>
		);
	}

	if (!isConnected) {
		return (
			<Modal {...modalProps} useDefaultHeader={true}>
				<div className={css(styleSheet.errorContainer)}>
					<p>Please connect to Meta Ads first.</p>
					<Button variant='secondary' onClick={handleClose}>
						Close
					</Button>
				</div>
			</Modal>
		);
	}

	return (
		<Modal {...modalProps} useDefaultHeader={true}>
			<div className={css(styleSheet.content)}>
				{step === 'adAccount' && (
					<section className={css(styleSheet.stepContainer)}>
						<h2 className={css(styleSheet.stepTitle)}>Select Ad Account</h2>

						{isLoadingAccounts ? (
							<div className={css(styleSheet.stepLoadingContainer)}>
								<LoadingSpinner />
							</div>
						) : accountsAuthFailureReason ? (
							<MetaAuthFailureNotice reason={accountsAuthFailureReason} onReconnect={onReconnect} />
						) : accountsError ? (
							<div className={css(styleSheet.errorText)}>Failed to load ad accounts. Please try again.</div>
						) : !adAccounts || adAccounts.length === 0 ? (
							<div className={css(styleSheet.emptyState)}>No ad accounts found</div>
						) : (
							<div className={css(styleSheet.radioGroup)}>
								{adAccounts.map(account => (
									<label key={account.id} className={css(styleSheet.radioLabel)}>
										<RadioButton
											checked={selectedAdAccountId === account.id}
											onChange={() => setSelectedAdAccountId(account.id)}
											className={css(styleSheet.radioInput)}
										>
											<div className={css(styleSheet.accountName)}>{account.name}</div>
											{account.businessName && (
												<div className={css(styleSheet.businessName)}>{account.businessName}</div>
											)}
											{account.externalAccountId && (
												<div className={css(styleSheet.accountId)}>ID: {account.externalAccountId}</div>
											)}
										</RadioButton>
									</label>
								))}
							</div>
						)}
					</section>
				)}

				{step === 'pages' && (
					<section className={css(styleSheet.stepContainer)}>
						<h2 className={css(styleSheet.stepTitle)}>Select Pages</h2>
						<p className={css(styleSheet.stepDescription)}>
							Choose which Facebook pages to receive leads from. Only leads from selected pages will be ingested.
						</p>

						{isLoadingPages ? (
							<div className={css(styleSheet.stepLoadingContainer)}>
								<LoadingSpinner />
							</div>
						) : pagesAuthFailureReason ? (
							<MetaAuthFailureNotice reason={pagesAuthFailureReason} onReconnect={onReconnect} />
						) : pagesError ? (
							<div className={css(styleSheet.errorText)}>Failed to load pages. Please try again.</div>
						) : !pages || pages.length === 0 ? (
							<div className={css(styleSheet.emptyState)}>No pages found</div>
						) : (
							<div className={css(styleSheet.checkboxGroup)}>
								{pages.map(page => (
									<label key={page.id} className={css(styleSheet.checkboxLabel)}>
										<Checkbox
											checked={selectedPageIds.has(page.id)}
											onCheckedChange={() => handlePageToggle(page.id)}
										/>
										<div>
											<div className={css(styleSheet.accountName)}>{page.name}</div>
											{page.category && <div className={css(styleSheet.businessName)}>{page.category}</div>}
										</div>
									</label>
								))}
							</div>
						)}
					</section>
				)}
			</div>
			<footer className={css(styleSheet.buttonGroup)}>
				{step === 'adAccount' && (
					<>
						<Button variant='primary' onClick={handleNext} disabled={!selectedAdAccountId || isLoadingAccounts}>
							Next
						</Button>
						<Button variant='secondary' onClick={handleClose}>
							Cancel
						</Button>
					</>
				)}

				{step === 'pages' && (
					<>
						<Button
							variant='primary'
							onClick={handleSave}
							disabled={selectedPageIds.size === 0 || isLoadingPages || isSaving}
							isLoading={isSaving}
						>
							{isSaving ? 'Saving...' : 'Save'}
						</Button>
						<Button variant='secondary' onClick={handleBack}>
							Back
						</Button>
					</>
				)}
			</footer>
		</Modal>
	);
};
